/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineSeparator';

const TimelineSeparatorManager: ComponentManagerFn<'TimelineSeparator'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TimelineSeparator'>(platform, 'TimelineSeparator'),
    type: 'TimelineSeparator',
    name: 'Timeline Separator',
    defaultChildren: ['TimelineDot', 'TimelineConnector'],
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          ['TriggerOnClick', 'TimelineDot', 'TimelineConnector'] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }

      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return (childType === 'TimelineConnector' && children.length === 1) ||
        children.length === 0
        ? true
        : 'Already added';
    },

    defaultValue,
    props: {},
    getDisplayText: (item) => 'Timeline Separator',
    category: 'Forms',
    icon: { iconName: 'arrows-h' },
    description: `**Overview**<br/>
The Timeline Separator is a structural container component used exclusively within **Timeline** visualizations. Its primary purpose is to hold and align the visual markers of the timeline—specifically the **Dot** and the **Connector** line.
It acts as the central vertical column in a timeline row, positioning the event marker visually between the "Opposite Content" (left/top) and the "Main Content" (right/bottom).

**1. Structural Role**<br/>
This component does not have visual properties of its own (like color or size); rather, it acts as a wrapper layout to define the vertical axis.
* **Container:** It groups the **TimelineDot** and **TimelineConnector** together so they stack vertically and align correctly with the surrounding text content.
* **Default Elements:** When added, it typically includes a **Timeline Dot** and **Timeline Connector** as default children, forming the basic "spine" of the timeline.

**2. Supported Children**<br/>
To function correctly, the Separator must contain specific child components that define the visual path:
* **Timeline Dot:** The circular indicator for the specific event.
* **Timeline Connector:** The vertical line connecting this event to the next one in the sequence.
* **TriggerOnClick:** Allows the entire separator area (the line and dot combined) to be interactive.

**3. Configuration**<br/>
* **Zero Config:** There are no configurable properties on the Separator itself. All visual styling (such as colors, borders, icons, or line thickness) must be applied directly to the child **Dot** or **Connector** components.

**4. Best Use Cases**<br/>
* **Standard Timelines:** This is a mandatory component for building any connected timeline. Every **TimelineItem** requires a Separator to render the central axis properly.
* **Process Flowcharts:** Ensuring that dots and lines remain perfectly centered between step descriptions and timestamps.`,
  };
};

export default TimelineSeparatorManager;
